
/**
 * Load initial data pipeline extracted from MainApp.loadInitialData
 */
export async function loadInitialData(ctx, {
    filesCacheManager,
    searchEngine,
    fileListManager,
    // typeahead removed
    unzip
}) {
    try {
        // Early language init to localize loader statuses
        try {
            if (!window.LANG) {
                window.LANG = (window.LANG_FROM_SERVER || window.langCheckReload('') || window.LANG_BROWSER || 'EN').toString().toUpperCase();
            }
        } catch(_) { window.LANG = window.LANG || 'EN'; }

        // ✅ CRITICAL: Register component status with weight (percentage)
        if(window.ComponentLoadingStatus){
            const filesCaption = (typeof translate==='function'? translate('loader_stage_loading_files'): 'Loading files...');
            window.ComponentLoadingStatus.register('filesStore', 'loading', filesCaption || 'Loading files...', 25);
        }
        
        // Show loading state
        ctx.showLoading(true);


        // Always fetch from server on page load - browser caching handles subsequent requests
        // Force refresh when auth state changes (guest -> auth or auth -> guest)
        const currAuth = !!(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated);
        let prevAuth = null;
        try { prevAuth = JSON.parse(localStorage.getItem('filesAuthState') || 'null'); } catch(_) { prevAuth = null; }
        const authChanged = (prevAuth !== null) ? (prevAuth !== currAuth) : false;
        
        // Always force update on page load to ensure fresh data
        const mustForceUpdate = true;
        // Always show progress bar
        ctx._showProgress = true;
        ctx.toggleLoaderProgress(true);
        ctx.updateLoaderProgress(5, (typeof translate==='function'? translate('loader_stage_check'): 'Checking updates…'));
        // Show loader while fetching and saving
        ctx.showLoading(true);
        // Clear local cache on auth switch to avoid serving public list after login
        if (authChanged) {
            try { localStorage.removeItem('filesList'); localStorage.removeItem('filesDate'); } catch(_) {}
        }
        
        // Fetch user country for restriction filtering
        let userCountry = null;
        try {
            const countryResponse = await fetch('/api/files/user-country');
            if (countryResponse.ok) {
                const countryData = await countryResponse.json();
                if (countryData && countryData.data && countryData.data.country) {
                    userCountry = countryData.data.country;
                    // Cache country in sessionStorage
                    try { sessionStorage.setItem('userCountry', userCountry); } catch(_) {}
                }
            }
        } catch (e) {
            // Try to get from sessionStorage if API fails
            try { userCountry = sessionStorage.getItem('userCountry'); } catch(_) {}
            if (debugMode === '1') {
                console.warn('[loadInitialData] Failed to fetch user country:', e);
            }
        }
        
        const result = await filesCacheManager.loadFiles({
            url: '/api/files/', // CRITICAL: Direct API URL to prevent corruption
            forceUpdate: true,  // Always fetch from server on page load
            skipCacheCheck: false,  // Check cache but always fetch on initial load
            processFiles: true,
            processingOptions: {
                sanitize: true,
                filterConfidential: !(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated),
                filterCountryRestrictions: true,
                userCountry: userCountry
            },
            onProgress: (evt) => {
                try {
                    if (evt && evt.type === 'download' && typeof evt.percent === 'number') {
                        const p = Math.max(5, Math.min(55, Math.round(5 + (evt.percent * 0.5))));
                        ctx.updateLoaderProgress(p, (typeof translate==='function'? translate('loader_stage_download_files'): 'Downloading files…'));
                    }
                } catch(_){ }
            }
        });

        
        // ❌ CRITICAL: DO NOT update component status here!
        // Status will be updated AFTER HTTP request completes in FileLoader.loadFromServer()
        // This ensures spinner stays visible until ALL /api/files/ requests are truly finished

        if (Array.isArray(result.files) && result.files.length > 0) {
            // Expose OT access flag globally for future use
            try {
                window.__has_ot_access = !!result.hasOtAccess;
            } catch(_) { /* noop */ }
            // Track server timestamp to detect updates
            ctx.lastFilesTimestamp = result.timestamp || result.serverTimestamp || Date.now();
            // Persist current auth state to detect future changes
            try { localStorage.setItem('filesAuthState', JSON.stringify(currAuth)); } catch(_) {}
            // Unified store only (no legacy globals)
            try {
                
                if (window.filesStore && typeof window.filesStore.setFiles === 'function') {
                    window.filesStore.setFiles(result.files);
                    
                    // ✅ Component status already updated above after loadFiles() completed
                    
                    // ✅ Emit filesLoaded event for LoaderManager integration (like audio lectures page)
                    try {
                        const filesLoadedEvent = new CustomEvent('filesLoaded', {
                            detail: {
                                filesCount: result.files.length,
                                source: result.source,
                                timestamp: Date.now()
                            }
                        });
                        document.dispatchEvent(filesLoadedEvent);
                    } catch(_) {}
                }
                // Cache meta snapshot globally for picker bounds
                try { window.__idbMetaCache = (await (async()=>{ try { const db = await (await import('../storage/idb-files.js')).openDatabase(); const meta = await (await import('../storage/idb-files.js')).getMeta(db); (await import('../storage/idb-files.js')).closeDatabase(db); return meta; } catch(_) { return null; }})()); } catch(_) {}
                
                // Load filter metadata from IndexedDB and populate filters
                try {
                    const { openDatabase, closeDatabase } = await import('../storage/idb-files.js');
                    const db = await openDatabase();
                    try {
                        // Use Promise-based API instead of callback
                        const tx = db.transaction('meta', 'readonly');
                        const store = tx.objectStore('meta');
                        
                        // Wrap IDB request in Promise
                        const filterMeta = await new Promise((resolve, reject) => {
                            const req = store.get('filter_metadata');
                            req.onsuccess = () => resolve(req.result);
                            req.onerror = () => reject(req.error || new Error('Failed to read filter_metadata'));
                        });
                        
                        if (filterMeta && filterMeta.uniqueValues) {
                            // Expose filter metadata globally
                            window.__filterMetadata = filterMeta;
                            
                            // Wait a bit to ensure filterController is fully initialized
                            await new Promise(resolve => setTimeout(resolve, 100));
                            
                            // Trigger filter refresh if filterController exists and not already refreshing
                            if (window.filterController && typeof window.filterController.refreshFiltersFromMetadata === 'function') {
                                if (!window.filterController._isRefreshing) {
                                    window.filterController.refreshFiltersFromMetadata(filterMeta);
                                }
                            }
                        }
                    } finally {
                        closeDatabase(db);
                    }
                } catch (metaErr) {
                    if (debugMode === '1') {
                        console.error('[loadInitialData] Exception loading filter metadata:', metaErr);
                    }
                    if (window.HyperLog) { window.HyperLog.warn('[loadInitialData] Failed to load filter metadata', { error: metaErr?.message }); }
                }
            } catch(e) { /* noop */ }

            // Build search index
            const indexLabel = result.source === 'cache' 
                ? (typeof translate==='function'? translate('loader_stage_building_index'): 'Building search index…')
                : (typeof translate==='function'? translate('loader_stage_idb_files'): 'Saving files…');
            ctx.updateLoaderProgress(60, indexLabel);
            searchEngine.buildIndex(result.files);

            // Periodic polling removed - browser caching via Cache-Control handles updates

            // Typeahead removed: skip any initialization

            // Check if there are ANY filters in URL (sq, cat, date-from, date-to) - if so, apply filters BEFORE displaying files
            // This prevents showing initial files and then filtering them out
            let hasUrlFilters = false;
            try {
                // Check for search query
                const urlQuery = (window.$ && typeof window.$.urlParam === 'function') ? window.$.urlParam('sq') : null;
                const filterQuery = (window.filterController && window.filterController.activeFilters) 
                    ? window.filterController.activeFilters.searchQuery 
                    : null;
                const searchQuery = (urlQuery && typeof urlQuery === 'string' && urlQuery.trim()) || (filterQuery && typeof filterQuery === 'string' && filterQuery.trim()) || null;
                
                // Check for category filters
                const urlCategories = (window.$ && typeof window.$.urlParam === 'function') ? window.$.urlParam('cat') : null;
                const hasCategories = !!(urlCategories && typeof urlCategories === 'string' && urlCategories.trim());
                
                // Check for date filters
                const urlDateFrom = (window.$ && typeof window.$.urlParam === 'function') ? window.$.urlParam('date-from') || window.$.urlParam('dateFrom') : null;
                const urlDateTo = (window.$ && typeof window.$.urlParam === 'function') ? window.$.urlParam('date-to') || window.$.urlParam('dateTo') : null;
                const hasDates = !!(urlDateFrom || urlDateTo);
                
                // Check for public only filter (pu parameter or default for guests) - only for guests
                const isAuthenticated = !!(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated);
                const isGuest = !isAuthenticated;
                const urlPu = isGuest && (window.$ && typeof window.$.urlParam === 'function') ? window.$.urlParam('pu') : null;
                // Check URL parameter first, then filterManager.activeFilters (set by loadSavedFilters with default true for guests)
                let hasPublicOnly = !!(urlPu === '1' || urlPu === 1);
                // If no URL parameter, check filterManager.activeFilters (set by loadSavedFilters with default true for guests)
                if (!hasPublicOnly && isGuest && window.filterManager && window.filterManager.activeFilters) {
                    hasPublicOnly = !!(window.filterManager.activeFilters.publicOnly);
                }
                
                // Check if any filters are active in filterController
                const hasActiveFilters = window.filterController && window.filterController.activeFilters
                    ? (window.filterController.activeFilters.searchQuery || 
                       (window.filterController.getSelectedCategories && Object.keys(window.filterController.getSelectedCategories() || {}).some(k => {
                           const cats = window.filterController.getSelectedCategories()[k];
                           return Array.isArray(cats) && cats.length > 0;
                       })) ||
                       window.filterController.activeFilters.dateFrom ||
                       window.filterController.activeFilters.dateTo ||
                       window.filterController.activeFilters.publicOnly)
                    : false;
                
                // Determine if we have any filters to apply
                // For guests, always apply filters if publicOnly is set (even without URL parameter) to enable random selection
                hasUrlFilters = !!(searchQuery || hasCategories || hasDates || hasPublicOnly || hasActiveFilters);
                
                if (hasUrlFilters) {
                    
                    // Show loader while applying filters
                    ctx.updateLoaderProgress(85, (typeof translate==='function'? translate('loader_stage_applying_filters') || 'Applying filters…' : 'Applying filters…'));
                    ctx.showLoading(true);
                    
                    // Ensure search input is set if there's a search query
                    if (searchQuery) {
                        try {
                            const $searchInput = window.jQuery ? window.jQuery('#fnd_searchbox, input[name="sq"], input[name="search"]').first() : null;
                            if ($searchInput && $searchInput.length && !$searchInput.val()) {
                                $searchInput.val(searchQuery);
                            }
                        } catch(_) {}
                    }
                    
                    // Ensure date inputs are set if there are date filters
                    if (hasDates) {
                        try {
                            if (urlDateFrom) {
                                const $dateFrom = window.jQuery ? window.jQuery('.filter-date-from') : null;
                                if ($dateFrom && $dateFrom.length && !$dateFrom.val()) {
                                    $dateFrom.val(urlDateFrom);
                                }
                            }
                            if (urlDateTo) {
                                const $dateTo = window.jQuery ? window.jQuery('.filter-date-to') : null;
                                if ($dateTo && $dateTo.length && !$dateTo.val()) {
                                    $dateTo.val(urlDateTo);
                                }
                            }
                        } catch(_) {}
                    }
                    
                    // Ensure public only checkbox is set if pu=1 parameter is present OR if it's default for guests
                    if (hasPublicOnly) {
                        try {
                            const publicOnlyCheckbox = document.getElementById('public-only-filter');
                            if (publicOnlyCheckbox && !publicOnlyCheckbox.checked) {
                                publicOnlyCheckbox.checked = true;
                                // Update filterManager activeFilters
                                if (window.filterManager) {
                                    window.filterManager.activeFilters.publicOnly = true;
                                }
                            }
                        } catch(_) {}
                    }
                    
                    // Wait a bit for filterController to initialize categories from URL if needed
                    // Categories are applied asynchronously in refreshFiltersFromMetadata
                    if (hasCategories && window.filterController && !window.filterController._urlCategoriesApplied) {
                        await new Promise(resolve => setTimeout(resolve, 300));
                    }
                    
                    // Apply filters immediately to prevent showing unfiltered files
                    if (window.filterManager && typeof window.filterManager.applyFilters === 'function') {
                        // ✅ CRITICAL: Mark filesFiltered as loading before applying filters
                        if (window.ComponentLoadingStatus) {
                            try {
                                const filtersCaption = (typeof translate==='function'? translate('loader_stage_applying_filters'): 'Applying filters...');
                                window.ComponentLoadingStatus.update('filesFiltered', 'loading', filtersCaption || 'Applying filters...');
                            } catch(_) {}
                        }
                        // Filters will call lazyLoadManager.setFiles() with filtered results
                        // Don't update URL during initial load
                        window.filterManager.applyFilters(false);
                        // Wait a bit longer for filters to apply and files to render (fileListRefreshDelay is 500ms)
                        await new Promise(resolve => setTimeout(resolve, 600));
                        // Filters will update statistics internally with filtered results
                        // Skip direct setFiles call - filters will handle it
                        // Note: filesFiltered status will be updated to 'ready' in lazyLoadManager.loadMore() after rendering
                    } else {
                        // Fallback: if filterManager not ready, apply filters manually
                        if (debugMode === '1') {
                            console.warn('[loadInitialData] filterManager not ready, will display files without pre-filtering');
                        }
                        // Display files normally - filters will be applied on next cycle
                        if (window.lazyLoadManager) {
                            window.lazyLoadManager.setFiles(result.files);
                        } else {
                            fileListManager.setFiles(result.files);
                        }
                        // Update statistics for unfiltered files
                        ctx.updateStatistics(result.files);
                        // ✅ CRITICAL: Mark filesFiltered as ready if no filters are active
                        if (window.ComponentLoadingStatus) {
                            try {
                                const displayedCaption = (typeof translate==='function'? translate('loader_stage_files_displayed'): 'Files displayed (no filters)');
                                window.ComponentLoadingStatus.update('filesFiltered', 'ready', displayedCaption || 'Files displayed (no filters)');
                            } catch(_) {}
                        }
                    }
                    
                    // Update progress to indicate filtering is complete
                    // Progress will continue to 100% when summaries are loaded
                    ctx.updateLoaderProgress(85, (typeof translate==='function'? translate('loader_stage_applying_filters') || 'Filters applied' : 'Filters applied'));
                } else {
                    // No filters - display files normally
                    if (window.lazyLoadManager) {
                        window.lazyLoadManager.setFiles(result.files);
                    } else {
                        fileListManager.setFiles(result.files);
                    }
                    // Update statistics for all files
                    ctx.updateStatistics(result.files);
                    // ✅ CRITICAL: Mark filesFiltered as ready if no filters are active
                    if (window.ComponentLoadingStatus) {
                        try {
                            let caption = 'Files displayed (no filters)';
                            if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                              const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                              caption = window.TranslationRegistry.translate('loader_stage_files_displayed', lang);
                            }
                            window.ComponentLoadingStatus.update('filesFiltered', 'ready', caption);
                        } catch(_) {}
                    }
                }
            } catch(err) {
                if (debugMode === '1') {
                    console.error('[loadInitialData] Error checking URL filters:', err);
                }
                // Fallback: display files normally if filter check fails
                if (window.lazyLoadManager) {
                    window.lazyLoadManager.setFiles(result.files);
                } else {
                    fileListManager.setFiles(result.files);
                }
                // Update statistics for all files
                ctx.updateStatistics(result.files);
            }
            
            // Initial data loaded logging only in debug mode

            // Restore audio player state if saved (after files are loaded)
            try {
                // Use dynamic import to ensure audio-player module is loaded
                const audioPlayerModule = await import('../files/file-list.dom/audio-player.js');
                if (audioPlayerModule.restoreAudioPlayerState && typeof audioPlayerModule.restoreAudioPlayerState === 'function') {
                    // Wait a bit for files to be fully processed
                    setTimeout(() => {
                        audioPlayerModule.restoreAudioPlayerState().catch(err => {
                            console.warn('[loadInitialData] Failed to restore audio player state:', err);
                        });
                    }, 500);
                }
            } catch (e) {
                console.warn('[loadInitialData] Error restoring audio player state:', e);
            }

            // Load summaries dataset
            try {
                // ✅ Register summaries component for loader status
                if(window.ComponentLoadingStatus){
                    const summariesCaption = (typeof translate==='function'? translate('loader_stage_download_summaries'): 'Loading summaries…');
                    window.ComponentLoadingStatus.register('summaries', 'loading', summariesCaption || 'Loading summaries…', 5);
                }
                
                // loadSummaries() internally checks cache validity, no need to call isSummariesValid() separately
                ctx.updateLoaderProgress(65, (typeof translate==='function'? translate('loader_stage_check_summaries'): 'Checking summaries…'));
                // Use versioned URL from cache manifest for summaries
                const summariesUrl = (window.getVersionedUrl && typeof window.getVersionedUrl === 'function') 
                    ? window.getVersionedUrl('/api/files/summaries') 
                    : '/api/files/summaries';
                const summariesRes = await filesCacheManager.loadSummaries({ url: summariesUrl });
                ctx._summariesMeta = { timestamp: summariesRes.timestamp, source: summariesRes.source };
                
                // ✅ Update summaries component status to ready
                if(window.ComponentLoadingStatus){
                    const summariesLoadedCaption = (typeof translate==='function'? translate('loader_stage_summaries_loaded'): 'Summaries loaded');
                    window.ComponentLoadingStatus.update('summaries', 'ready', summariesLoadedCaption || 'Summaries loaded');
                }
                
                // Check if summaries were loaded from server (not from cache)
                if (summariesRes.source === 'server') {
                    ctx.updateLoaderProgress(95, (typeof translate==='function'? translate('loader_stage_download_summaries'): 'Summaries…'));
                }
                ctx.updateLoaderProgress(100, (typeof translate==='function'? translate('loader_stage_done'): 'Done'));
            } catch (e) {
                if (window.HyperLog) { window.HyperLog.warn('[MainApp] Summaries load warning', { error: e?.message }); }
                
                // ✅ Update summaries component status to error on failure
                if(window.ComponentLoadingStatus){
                    const summariesFailedCaption = (typeof translate==='function'? translate('loader_stage_summaries_failed'): 'Summaries load failed');
                    window.ComponentLoadingStatus.update('summaries', 'error', summariesFailedCaption || 'Summaries load failed');
                }
                
                // Complete progress even if summaries failed
                ctx.updateLoaderProgress(100, (typeof translate==='function'? translate('loader_stage_done'): 'Done'));
            }
            
            // If URL indicates full-text mode with a query, run initial full-text search AFTER everything is loaded
            try {
                const url = new URL(window.location.href);
                const hash = (url.hash || '').replace('#','');
                const sq = url.searchParams.get('sq') || '';
                const page = parseInt(url.searchParams.get('page') || '1', 10) || 1;
                
                
                if (hash === 'fulltext' && sq) {
                    if (debugMode === '1') {
                        console.log('[loadInitialData] Fulltext search triggered, checking dependencies...');
                    }
                    // Wait a bit longer to ensure searchApi and appInitializationManager are ready
                    setTimeout(async () => {
                        try {
                            if (debugMode === '1') {
                                console.log('[loadInitialData] Fulltext search timeout fired, checking:', {
                                    hasSearchApi: !!window.searchApi,
                                    hasAppInit: !!window.appInitializationManager,
                                    hasMethod: !!(window.appInitializationManager && typeof window.appInitializationManager.performFulltextSearch === 'function'),
                                    hasAxios: !!window.axios
                                });
                            }
                            
                            if (window.searchApi && window.appInitializationManager && typeof window.appInitializationManager.performFulltextSearch === 'function') {
                                if (debugMode === '1') {
                                    console.log('[loadInitialData] Executing fulltext search:', sq);
                                }
                                await window.appInitializationManager.performFulltextSearch(sq, page);
                                if (debugMode === '1') {
                                    console.log('[loadInitialData] Fulltext search completed');
                                }
                            } else {
                                if (debugMode === '1') {
                                    console.warn('[loadInitialData] Fulltext search skipped - dependencies not ready', {
                                        hasSearchApi: !!window.searchApi,
                                        hasAppInit: !!window.appInitializationManager,
                                        hasMethod: !!(window.appInitializationManager && typeof window.appInitializationManager.performFulltextSearch === 'function'),
                                        hasAxios: !!window.axios
                                    });
                                }
                                // Retry after additional delay if dependencies aren't ready yet
                                setTimeout(async () => {
                                    try {
                                        if (window.searchApi && window.appInitializationManager && typeof window.appInitializationManager.performFulltextSearch === 'function') {
                                            if (debugMode === '1') {
                                                console.log('[loadInitialData] Retry: Executing fulltext search:', sq);
                                            }
                                            await window.appInitializationManager.performFulltextSearch(sq, page);
                                        }
                                    } catch(err) {
                                        if (debugMode === '1') {
                                            console.error('[loadInitialData] Error in retry fulltext search:', err);
                                        }
                                    }
                                }, 1000);
                            }
                        } catch(err) {
                            if (debugMode === '1') {
                                console.error('[loadInitialData] Error in fulltext search:', err);
                            }
                        }
                    }, 500); // Increased delay to ensure everything is ready
                }
            } catch(err) { 
                if (debugMode === '1') {
                    console.error('[loadInitialData] Error checking URL for fulltext search:', err);
                }
            }
        } else {
            throw new Error('No files data received from Phase 4 cache manager');
        }

    } catch (error) {
        if (debugMode === '1') {
            console.error('[loadInitialData] Phase 4 loading failed:', error);
        }
        // Try fallback loading with legacy method
        if (debugMode === '1') {
            console.warn('[loadInitialData] Attempting fallback loading...');
        }
        try {
            const response = await fetch('/api/files/'); // CRITICAL: Direct API URL
            if (!response.ok) {
                throw new Error(`Fallback also failed: ${response.statusText}`);
            }
            const compressedData = await response.text();
            const data = unzip(compressedData);
            if (Array.isArray(data) && data.length > 0) {
                const files = ctx.processFileData(data);
                try { 
                    if (window.filesStore && typeof window.filesStore.setFiles === 'function') { 
                        window.filesStore.setFiles(files);
                        
                        // ❌ CRITICAL: DO NOT update component status here!
                        // The status will be updated by AJAX interception ONLY after the actual HTTP request completes
                    } 
                } catch(e) { /* noop */ }
                searchEngine.buildIndex(files);
                if (window.lazyLoadManager) {
                    window.lazyLoadManager.setFiles(files);
                } else {
                fileListManager.setFiles(files);
                }
                ctx.updateStatistics(files);
                if (debugMode === '1') {
                    console.log(`Fallback loading successful: ${files.length} files`);
                }
            } else {
                throw new Error('Fallback loading failed - no data');
            }
        } catch (fallbackError) {
            if (debugMode === '1') {
                console.error('[loadInitialData] All loading methods failed:', fallbackError);
            }
            ctx.showError('Не удалось загрузить файлы. Пожалуйста, обновите страницу.');
        }
    } finally {
        ctx.showLoading(false);
    }
}


